/**
 * What a service's folder says about it, for the Overview: its top-level names (one `readTree`, depth 1), its
 * `.flow/service.json` and the few files serviceStackModel reads (filesToRead: at most MAX_READS), read together;
 * a file that cannot be read is simply missing. `{ loading, names, specText, spec, files }`.
 */
import * as vfsClient from '../../../utils/vfsClient';
import { filesToRead, parseSpec } from '../../../utils/serviceStackModel';
import { useLoaded } from './common';

export async function loadServiceFacts(projectId, service) {
  const folder = `services/${service}`;
  const tree = await vfsClient.readTree(projectId, folder, 1).catch(() => null);
  const names = (tree?.children || []).map((child) => child.name);
  const hasSpec = (tree?.children || []).some((child) => child.name === '.flow' && child.type === 'directory');
  const wanted = filesToRead(names);
  const [specText, ...texts] = await Promise.all([
    hasSpec || !tree ? vfsClient.readFile(projectId, `${folder}/.flow/service.json`).catch(() => null) : null,
    ...wanted.map((file) => vfsClient.readFile(projectId, `${folder}/${file}`).catch(() => null)),
  ]);
  const files = Object.fromEntries(wanted.map((file, index) => [file, texts[index]]).filter(([, text]) => typeof text === 'string'));
  return { names, specText: typeof specText === 'string' ? specText : null, spec: parseSpec(specText), files };
}

export default function useServiceFacts(projectId, service) {
  const state = useLoaded(() => loadServiceFacts(projectId, service), [projectId, service]);
  return { loading: state.loading, names: [], specText: null, spec: null, files: {}, ...(state.value || {}) };
}
